import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Circle,
  HStack,
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  Textarea,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { FaArrowUp } from "react-icons/fa";
import { continueSession, initializeChatSession } from "services/AgentChatService";
import MessageComponent from "./MessageComponent";
import { AgentInputProto, ArtifactProto, ChatClientType, chatClientTypeToJSON, ChatContext, ChatMessageProto, ChatResponse, ResponseMessageProto } from "build-ts/chat_session";
import { ArtifactType, artifactTypeFromJSON, ChatMessageType, chatMessageTypeFromJSON, DarwinAgentType, DarwinHeader } from "build-ts/common";
import { Header } from "build-ts/metadata";
import { AvatarWithMemo, rotateAnimation } from "components/navbar/Navbar";
import logo from "../../assets/logo.png";
import { Menu, MenuButton, MenuList, MenuItem, Portal } from "@chakra-ui/react";
import { BsRobot } from "react-icons/bs";
import { generateRandomSuffix, getRandomWelcomeMessage } from "utils/nameUtils";
import { getAnalyst } from "services/AnalystService";
import showToast from "hooks/useCustomToast";
import { ChatRequest } from "build-ts/chat_session";
import { useNavigate } from "react-router-dom";
import { AnalystProto } from "build-ts/analyst";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { AiFillProject } from "react-icons/ai";
import { useAnalystChat } from "context/AnalystChatContext";

interface ChatProps {
  isLoadingChat: boolean;
  chatId: string | undefined;
  setChatId: React.Dispatch<React.SetStateAction<string | undefined>>;
  chatMessages: ChatMessage[];
  setChatMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  sessionId?: string;
  fetchChatSessionsList?: (clientType: ChatClientType, searchQuery?: string, analyst?: Header | null) => Promise<void>;
  fetchEvaChatSessionsList?: (searchQuery?: string) => Promise<void>;
  // Artifact click handler
  onArtifactClick?: (artifact: ArtifactProto) => void;
  onLanggraphMessageClick?: (langgraphMessageJsonList: string[]) => void;
  selectedAnalyst?: Header | null;
  // clean content function
  cleanContent?: (content: string) => string;
  // Analyst selection props
  analystsList?: Header[];
  isLoadingAnalysts?: boolean;
  // Eva flags, true when rendered from any two components of Eva (both Saved and Unsaved chat)
  isFromEva?: boolean;
  onAnalystChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  isArtifactsPanelVisible?: boolean;
  // All artifacts from all messages for dataframe link matching
  allArtifacts?: ArtifactProto[];
  setAllArtifacts?: React.Dispatch<React.SetStateAction<ArtifactProto[]>>;
  // Chat is saved if session header has created_date
  isChatSaved?: boolean;
  isForScenarioCreationAgentChat?: boolean;
  currentAnalyst?: AnalystProto;
  // Selected scenario ids for the menu: empty/undefined = all scenarios, otherwise multi-scenario context
  selectedScenarioIds?: string[];
  // Scenario selection props (when isForScenarioCreationAgentChat is true)
  scenariosList?: Header[];
  isLoadingScenarios?: boolean;
  // Called when user selects scenarios from the menu
  onScenarioChange?: (scenarioIds: string[]) => void;
  /** When set (e.g. from scenario search bar), initializes session and sends this as first message */
  initialPrompt?: string;
};

export interface ChatMessage {
  author: "user" | "ai";
  content: string;
  artifacts?: ArtifactProto[] | undefined;
  responseMessage?: ResponseMessageProto | undefined;
  selectedAnalystId?: string;
  ls_trace_url?: string;
  langgraph_message_json_list?: string[];
}

const Chat: React.FC<ChatProps> = ({
  isLoadingChat,
  chatId,
  setChatId,
  chatMessages,
  setChatMessages,
  sessionId,
  fetchChatSessionsList,
  fetchEvaChatSessionsList,
  onArtifactClick,
  onLanggraphMessageClick,
  selectedAnalyst,
  isFromEva = false,
  cleanContent,
  // Analyst selection props
  analystsList = [],
  isLoadingAnalysts = false,
  onAnalystChange,
  isArtifactsPanelVisible = false,
  allArtifacts = [],
  setAllArtifacts,
  isChatSaved,
  isForScenarioCreationAgentChat = false,
  currentAnalyst,
  selectedScenarioIds = [],
  scenariosList = [],
  isLoadingScenarios = false,
  onScenarioChange,
  initialPrompt,
}) => {
  const { setCurrentSessionHeader } = useAnalystChat();
  const [isLoading, setIsLoading] = useState(false);
  const [inputMessage, setInputMessage] = useState<string>("");
  const [welcomeMessage] = useState(() => getRandomWelcomeMessage()); // Generate welcome message on mount
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const userScrolledRef = useRef(false);
  const lastSentInitialPromptRef = useRef<string | null>(null);
  const navigate = useNavigate();
  const [isScenarioModalOpen, setIsScenarioModalOpen] = useState(false);
  const [scenarioModalValue, setScenarioModalValue] = useState<string[]>(selectedScenarioIds ?? []);

  const handleScroll = () => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      // Check if user is near bottom (within 100px)
      const isNearBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight < 50;

      // Only set userScrolledRef if user actively scrolled away from bottom
      if (!isNearBottom) {
        userScrolledRef.current = true;
      }
    }
  };

  const scrollToBottom = () => {
    if (messagesContainerRef.current && !userScrolledRef.current) {
      const container = messagesContainerRef.current;
      container.scrollTop = container.scrollHeight;
    }
  };

  // Auto-scroll only when new messages added AND user hasn't manually scrolled away
  useLayoutEffect(() => {
    if (!isLoadingChat && chatMessages.length > 0) {
      scrollToBottom();
    }
  }, [chatMessages, isLoadingChat]);

  // When initialPrompt is set (e.g. from scenario search bar), initialize session and send it once analyst is loaded
  useEffect(() => {
    if (
      !initialPrompt?.trim() ||
      !isForScenarioCreationAgentChat ||
      !currentAnalyst ||
      chatId !== undefined ||
      isLoading ||
      lastSentInitialPromptRef.current === initialPrompt
    ) {
      return;
    }
    lastSentInitialPromptRef.current = initialPrompt;
    handleSendMessage(true, null, initialPrompt.trim());
    // Intentionally omit handleSendMessage from deps: we only want to run when analyst/prompt/chatId/isLoading change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPrompt, isForScenarioCreationAgentChat, currentAnalyst, chatId, isLoading]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textarea = e.target;

    // Reset height so it can shrink if text is deleted
    textarea.style.height = "40px";

    // Set height based on content
    textarea.style.height = `${textarea.scrollHeight}px`;
    setInputMessage(e.target.value);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = async () => {
    if (chatId || sessionId) {
      // Continue existing chat
      await handleSendMessage();
    } else {
      // New chat
      await handleSendMessage(true, selectedAnalyst);
    }
  };

  const handleSendMessage = async (newChat: boolean = false, selectedAnalyst?: Header | null, messageOverride?: string) => {
    const messageToSend = (messageOverride !== undefined && messageOverride !== null ? messageOverride : inputMessage).trim();
    if (messageToSend !== "" && (chatId || newChat)) {
      const inputMsg = messageToSend;
      userScrolledRef.current = false;
      const userMsg = { content: inputMsg, author: "user" as const };
      const loadingMsg = { content: "", author: "ai" as const };

      // Add user message and loading AI message
      setChatMessages(prev => [...prev, userMsg, loadingMsg]);
      if (messageOverride === undefined) setInputMessage("");
      setIsLoading(true);

      try {
        let response: ChatResponse | undefined;
        if (isFromEva && sessionId) {
          // EVA playground: auto-save only if session was already saved (has created_date)
          response = await continueSession(sessionId, inputMsg, isChatSaved);
        } else if (newChat) {
          // New chat
          if (!selectedAnalyst && !isForScenarioCreationAgentChat) {
            showToast("Please select an analyst first", "", "warning");
            return;
          }
          const analystDetails = isForScenarioCreationAgentChat ? currentAnalyst : await getAnalyst(selectedAnalyst!.id!);
          if (!analystDetails) {
            showToast("Failed to load analyst details", "", "error");
            return;
          }
          // Create chat context (analyst chat or scenario creation)
          const chatContext = ChatContext.create({
            agent_type: isForScenarioCreationAgentChat ? DarwinAgentType.SCENARIO_CREATION_AGENT : DarwinAgentType.DATA_SCOPE_AGENT,
            agent_input: AgentInputProto.create({
              analyst: analystDetails
            })
          });

          // Create chat request
          const chatRequest: ChatRequest = ChatRequest.create({
            session_header: DarwinHeader.create({
              id: "",
              name: `Chat ${generateRandomSuffix(8)}`,
              type: chatClientTypeToJSON(ChatClientType.FRIDA)
            }),
            chat_context: chatContext,
            message: inputMsg,
            is_save: !isForScenarioCreationAgentChat
          });

          response = await initializeChatSession(chatRequest);
          if (response && response.session_header && response.session_header?.id) {
            setChatId(response.session_header.id);
            setCurrentSessionHeader(response?.session_header);
            if (!isForScenarioCreationAgentChat) {
              // Navigate to the new session URL - this changes chatSessionId from "new" to the actual ID
              navigate(`/chat/${response.session_header.id}`, { replace: true });
            }
          } else {
            showToast("Failed to create new chat session", "", "error");
          }
        } else {
          // Regular mode: auto-save enabled
          response = await continueSession(chatId!, inputMsg, !isForScenarioCreationAgentChat);
        }

        // Collect new artifacts from response.
        // When isForScenarioCreationAgentChat, only take artifacts from the last REASONING message
        // so we don't overwrite scenario cards from older reasoning.
        const newArtifacts: ArtifactProto[] = [];
        if (response && response.messages) {
          const reasoningMessages = response.messages.filter(
            (msg) =>
              chatMessageTypeFromJSON(msg.message_type) === ChatMessageType.REASONING && msg.artifacts
          );
          const messagesToProcess =
            isForScenarioCreationAgentChat && reasoningMessages.length > 0
              ? [reasoningMessages[reasoningMessages.length - 1]]
              : reasoningMessages;

          for (const msg of messagesToProcess) {
            for (const artifact of msg.artifacts!) {
              const artifactType = artifactTypeFromJSON(artifact.artifact_type);
              if (
                artifactType === ArtifactType.KG_DATAFRAME ||
                artifactType === ArtifactType.KG_OBSERVATION ||
                artifactType === ArtifactType.SCENARIO
              ) {
                newArtifacts.push(artifact);
              }
            }
          }
        }

        // Add new artifacts to the cumulative list.
        if (newArtifacts.length > 0 && setAllArtifacts) {
          if (isForScenarioCreationAgentChat) {
            // Only update scenario artifacts that appear in this response; keep existing ones (e.g. form-saved) for other ids
            const scenarioIdsInNew = new Set(
              newArtifacts
                .filter((a) => artifactTypeFromJSON(a.artifact_type) === ArtifactType.SCENARIO)
                .map((a) => (a as { scenario?: { id?: string } }).scenario?.id)
                .filter((id): id is string => id !== undefined && id !== null && id !== "")
                .map((id) => String(id))
            );
            setAllArtifacts((prev) =>
              prev
                .filter((a) => {
                  if (artifactTypeFromJSON(a.artifact_type) !== ArtifactType.SCENARIO) return true;
                  const raw = (a as { scenario?: { id?: string } }).scenario?.id;
                  const id = raw !== undefined && raw !== null ? String(raw) : "";
                  return !id || !scenarioIdsInNew.has(id);
                })
                .concat(newArtifacts)
            );
          } else {
            setAllArtifacts((prev) => [...prev, ...newArtifacts]);
          }
        }

        // Remove the loading message and add the actual AI response
        setChatMessages(prev => {
          const updated = prev.slice(0, -1);

          if (response && response.messages) {
            for (let i = response.messages.length - 1; i >= 0; i--) {
              const msg = response.messages[i] as ChatMessageProto;
              if (chatMessageTypeFromJSON(msg.message_type) === (ChatMessageType.RESPONSE) && msg?.content && msg?.content?.length > 0) {
                updated.push({ author: "ai", content: cleanContent ? cleanContent(msg.content) : msg.content || "", ls_trace_url: msg.ls_trace_url });
              }
              else if (chatMessageTypeFromJSON(msg.message_type) === (ChatMessageType.RESPONSE) && msg?.response_message) {
                updated.push({ author: "ai", content: "", responseMessage: msg.response_message, ls_trace_url: msg.ls_trace_url });
              }
              else if (chatMessageTypeFromJSON(msg.message_type) === (ChatMessageType.REASONING)) {
                // Since we iterate backwards (RESPONSE comes before REASONING in iteration order),
                // attach artifacts to the last AI message we pushed (include SCENARIO so user can click to open scenario)
                const artifactsForMessage = msg.artifacts;
                for (let j = updated.length - 1; j >= 0; j--) {
                  if (updated[j].author === "ai") {
                    updated[j].artifacts = artifactsForMessage && artifactsForMessage.length > 0 ? artifactsForMessage : undefined;
                    updated[j].ls_trace_url = msg.ls_trace_url;
                    updated[j].langgraph_message_json_list = msg.langgraph_message_json_list && msg.langgraph_message_json_list.length > 0 ? msg.langgraph_message_json_list : [];
                    break;
                  }
                }
              }
              else if (chatMessageTypeFromJSON(msg.message_type) === (ChatMessageType.HUMAN) && msg?.content && msg?.content?.length > 0) {
                if (msg.content === inputMsg) {
                  break; // break the loop if the user message is the same as the input message
                }
              }
            }
          }
          return updated;
        });

        // Only refresh sessions list when a new chat is created
        if (newChat && response?.session_header?.id) {
          if (isFromEva) {
            fetchEvaChatSessionsList?.("");
          } else {
            fetchChatSessionsList?.(ChatClientType.FRIDA, "");
          }
        }
      } catch (error) {
        // Remove loading message on error
        setChatMessages(prev => prev.slice(0, -1));
        console.error("Error sending message:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const hasMessages = chatMessages.length > 0;

  const handleScenarioSelect = (scenarioIds: string[]) => {
    const realIds = scenariosList.filter((s) => s.id !== "").map((s) => String(s.id ?? ""));
    const isAllSelected =
      realIds.length > 0 && realIds.every((id) => scenarioIds.includes(id));
    onScenarioChange?.(isAllSelected ? [] : scenarioIds);
    setIsScenarioModalOpen(false);
  };

  const openScenarioModal = () => {
    const ids = selectedScenarioIds ?? [];
    const realIds = scenariosList.filter((s) => s.id !== "").map((s) => String(s.id ?? ""));
    setScenarioModalValue(ids.length === 0 ? realIds : ids);
    setIsScenarioModalOpen(true);
  };

  const selectedScenarioLabel = (() => {
    const realScenarios = scenariosList.filter((s) => s.id !== "");
    const selected = selectedScenarioIds ?? [];

    if (
      selected.length === 0 ||
      (realScenarios.length > 0 &&
        realScenarios.every((s) => selected.includes(String(s.id ?? ""))))
    ) {
      return "All scenarios";
    }

    if (selected.length === 1) {
      return (
        realScenarios.find((s) => String(s.id ?? "") === selected[0])?.name ??
        "Select scenario"
      );
    }

    return `${selected.length} scenarios selected`;
  })();

  // Input area component - reused in both layouts
  const renderInputArea = (width: string) => (
    <Box
      w={width}
      border="2px solid"
      borderColor="gray.300"
      borderRadius="xl"
      bg="white"
      overflow="hidden"
      _focusWithin={{ borderColor: "purple.400", boxShadow: "0 0 0 1px var(--chakra-colors-purple-600)" }}
      boxShadow="sm"
    >
      {!hasMessages ? (
        <>
          <Textarea
            id={isForScenarioCreationAgentChat ? "scenario-chat-input" : "chat-input"}
            value={inputMessage}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            placeholder="How can I help?"
            resize="none"
            minHeight="90px"
            maxHeight="150px"
            border="none"
            _focus={{ boxShadow: "none" }}
            isDisabled={isLoadingChat || isLoading}
            fontSize="md"
            px={3}
            pt={3}
            pb={1}
            fontWeight="500"
          />
          <HStack justify="flex-end" spacing={2} m={2}>
            {!isFromEva && !isForScenarioCreationAgentChat && (
              isLoadingAnalysts ? (
                <Spinner size="xs" color="purple.500" />
              ) : (
                <Menu>
                  <MenuButton
                    as={Button}
                    size="sm"
                    minW="100px"
                    maxW="330px"
                    bg="purple.50"
                    border="none"
                    fontSize="sm"
                    fontWeight="500"
                    borderRadius="md"
                    color="gray.700"
                    _hover={{ bg: "purple.100" }}
                    _active={{ bg: "purple.100" }}
                    rightIcon={<ChevronDownIcon />}
                    textAlign="left"
                    px={3}
                  >
                    <HStack spacing={2}>
                      <Icon as={BsRobot} boxSize={5} color="purple.600" />
                      <Text isTruncated fontSize="sm" mb={0} fontWeight="500">
                        {selectedAnalyst?.name || "Select analyst"}
                      </Text>
                    </HStack>
                  </MenuButton>
                  <Portal>
                    <MenuList maxH="300px" overflowY="auto" zIndex={1500}>
                      {analystsList.length === 0 ? (
                        <MenuItem isDisabled fontSize="sm" color="gray.500">
                          No analysts available
                        </MenuItem>
                      ) : (
                        analystsList.map((analyst) => (
                          <MenuItem
                            key={analyst.id}
                            onClick={() => onAnalystChange?.({ target: { value: analyst.id } } as any)}
                            fontSize="sm"
                            _hover={{ bg: "purple.50" }}
                            _focus={{ bg: "purple.50" }}
                            fontWeight="500"
                          >
                            {analyst.name}
                          </MenuItem>
                        ))
                      )}
                    </MenuList>
                  </Portal>
                </Menu>
              )
            )}
            {isForScenarioCreationAgentChat && (
              <Tooltip label={isLoadingScenarios ? "Loading..." : selectedScenarioLabel} hasArrow placement="top">
                <Button
                  size="sm"
                  minW="100px"
                  maxW="330px"
                  bg="purple.50"
                  border="none"
                  fontSize="sm"
                  fontWeight="500"
                  borderRadius="md"
                  color="gray.700"
                  _hover={{ bg: "purple.100" }}
                  _active={{ bg: "purple.100" }}
                  rightIcon={<ChevronDownIcon />}
                  textAlign="left"
                  px={3}
                  onClick={openScenarioModal}
                  aria-label="Select scenario"
                >
                  <HStack spacing={2} overflow="hidden" minW={0}>
                    <Icon as={AiFillProject} boxSize={5} color="purple.600" flexShrink={0} />
                    <Text isTruncated fontSize="sm" mb={0} fontWeight="500">
                      {isLoadingScenarios ? "..." : selectedScenarioLabel}
                    </Text>
                  </HStack>
                </Button>
              </Tooltip>
            )}
            <IconButton
              icon={isLoading ? <Spinner size="xs" color="white" /> : <Icon as={FaArrowUp} boxSize={5} color="white" />}
              onClick={handleSend}
              bg="purple.600"
              isDisabled={isLoadingChat || isLoading}
              aria-label="Send Chat Message"
              borderRadius="full"
              size="md"
              _hover={{ bg: "purple.700" }}
              _disabled={{ bg: "gray.300", cursor: "not-allowed" }}
            />
          </HStack>
        </>
      ) : (
        // Not empty: Horizontal layout (textarea + button inline)
        <HStack spacing={2} align="flex-end" p={2}>
          <Textarea
            value={inputMessage}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            placeholder="Reply..."
            resize="none"
            minHeight="40px"
            maxHeight="150px"
            border="none"
            _focus={{ boxShadow: "none" }}
            isDisabled={isLoadingChat || isLoading}
            fontSize="md"
            px={2}
            py={2}
            flex="1"
          />
          <IconButton
            icon={isLoading ? <Spinner size="xs" color="white" /> : <Icon as={FaArrowUp} boxSize={4} color="white" />}
            onClick={handleSend}
            bg="purple.600"
            isDisabled={isLoadingChat || isLoading}
            aria-label="Send Chat Message"
            borderRadius="full"
            size="sm"
            _hover={{ bg: "purple.700" }}
            _disabled={{ bg: "gray.300", cursor: "not-allowed" }}
            flexShrink={0}
            alignSelf="center"
          />
          {/* Analyst Selection Menu */}
          {!isFromEva && !isForScenarioCreationAgentChat && (
            <Menu>
              <MenuButton
                as={IconButton}
                icon={<Icon as={BsRobot} boxSize={5} color="purple.600" />}
                size="sm"
                variant="ghost"
                borderRadius="full"
                bg="purple.50"
                _hover={{ bg: "purple.100" }}
                aria-label="Select analyst"
                flexShrink={0}
                alignSelf="center"
              />
              <Portal>
                <MenuList maxH="300px" overflowY="auto" zIndex={1500}>
                  {isLoadingAnalysts ? (
                    <MenuItem isDisabled>
                      <Spinner size="xs" color="purple.500" mr={2} />
                      Loading analysts...
                    </MenuItem>
                  ) : analystsList.length === 0 ? (
                    <MenuItem isDisabled fontSize="sm" color="gray.500">
                      No analysts available
                    </MenuItem>
                  ) : (
                    analystsList.map((analyst) => (
                      <MenuItem
                        key={analyst.id}
                        onClick={() => onAnalystChange?.({ target: { value: analyst.id } } as any)}
                        fontSize="sm"
                        _hover={{ bg: "purple.50" }}
                        _focus={{ bg: "purple.50" }}
                        fontWeight="500"
                        bg={selectedAnalyst?.id === analyst.id ? "purple.50" : undefined}
                      >
                        <HStack spacing={2}>
                          {selectedAnalyst?.id === analyst.id && (
                            <Icon as={BsRobot} boxSize={4} color="purple.600" />
                          )}
                          <Text mb={0}>{analyst.name}</Text>
                        </HStack>
                      </MenuItem>
                    ))
                  )}
                </MenuList>
              </Portal>
            </Menu>
          )}
          {isForScenarioCreationAgentChat && (
            <Tooltip label={isLoadingScenarios ? "Loading..." : selectedScenarioLabel} hasArrow placement="top">
              <IconButton
                icon={<Icon as={AiFillProject} boxSize={5} color="purple.600" />}
                size="sm"
                variant="ghost"
                borderRadius="full"
                bg="purple.50"
                _hover={{ bg: "purple.100" }}
                aria-label="Select scenario"
                onClick={openScenarioModal}
                flexShrink={0}
                alignSelf="center"
              />
            </Tooltip>
          )}
        </HStack>
      )}
      {/* Scenario selection modal (multi-select; "All scenarios" is default when none or all are selected) */}
      {isForScenarioCreationAgentChat && (
        <Modal isOpen={isScenarioModalOpen} onClose={() => setIsScenarioModalOpen(false)} size="md" isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Select scenario</ModalHeader>
            <ModalBody pb={6}>
              {isLoadingScenarios ? (
                <HStack justify="center" py={4}>
                  <Spinner size="sm" color="purple.500" />
                  <Text fontSize="sm" color="gray.500">Loading scenarios...</Text>
                </HStack>
              ) : (
                <CheckboxGroup
                  value={scenarioModalValue}
                  onChange={(values) => setScenarioModalValue(values as string[])}
                >
                  <VStack align="stretch" spacing={2}>
                    {scenariosList.length === 0 ? (
                      <Text fontSize="sm" color="gray.500">No scenarios available</Text>
                    ) : (
                      <>
                        {scenariosList.find((s) => s.id === "") && (
                          <Checkbox
                            key="all-scenarios"
                            isChecked={
                              scenarioModalValue.length > 0 &&
                              scenariosList
                                .filter((s) => s.id !== "")
                                .every((s) =>
                                  scenarioModalValue.includes(String(s.id ?? ""))
                                )
                            }
                            colorScheme="purple"
                            _hover={{ bg: "purple.50" }}
                            px={3}
                            py={2}
                            borderRadius="md"
                            onChange={(e) => {
                              const realIds = scenariosList
                                .filter((s) => s.id !== "")
                                .map((s) => String(s.id ?? ""));
                              setScenarioModalValue(
                                e.target.checked ? realIds : []
                              );
                            }}
                          >
                            All scenarios
                          </Checkbox>
                        )}
                        {scenariosList
                          .filter((scenario) => scenario.id !== "")
                          .map((scenario) => (
                            <Checkbox
                              key={scenario.id}
                              value={String(scenario.id ?? "")}
                              colorScheme="purple"
                              _hover={{ bg: "purple.50" }}
                              px={3}
                              py={2}
                              borderRadius="md"
                            >
                              {scenario.name}
                            </Checkbox>
                          ))}
                      </>
                    )}
                  </VStack>
                </CheckboxGroup>
              )}
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={() => setIsScenarioModalOpen(false)}>
                Cancel
              </Button>
              <Button
                colorScheme="purple"
                onClick={() => handleScenarioSelect(scenarioModalValue)}
                isDisabled={isLoadingScenarios || scenariosList.length === 0}
              >
                Select
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );

  // Loading state
  if (isLoadingChat) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" w="100%" h="100%">
        <Spinner size="md" color="purple.700" thickness="2px" mr={2} />
        <Text fontSize="md" color="gray.500" mb={0}>
          Loading...
        </Text>
      </Box>
    );
  }

  // Empty state - centered layout
  if (!hasMessages) {
    return (
      <VStack
        w="100%"
        h="100%"
        justify="center"
        align="center"
        spacing={9}
        mt={-3}
      >
        <HStack spacing={5}>
          <Circle bg="purple.50" p={2}>
            <AvatarWithMemo
              src={logo}
              animation={`${rotateAnimation} 3s linear infinite`}
              name="Anko"
              size="md"
            />
          </Circle>
          <Text color="gray.700" fontSize="3xl" fontWeight="400" m={0}>
            {!isForScenarioCreationAgentChat
              ? welcomeMessage
              : "Welcome! Can I help you to create/update scenarios?"}
          </Text>
        </HStack>
        {renderInputArea("700px")}
      </VStack>
    );
  }

  // With messages - bottom input layout
  return (
    <VStack w="100%" h="100%" m={0} spacing={0} mt={isForScenarioCreationAgentChat ? 8 : 1}>
      <Box
        ref={messagesContainerRef}
        flex="1"
        w="100%"
        overflow="auto"
        onScroll={handleScroll}
        p={4}
        css={{
          "&::-webkit-scrollbar": {
            display: "none"
          },
          scrollbarWidth: "none",
          msOverflowStyle: "none"
        }}
      >
        <VStack w={isArtifactsPanelVisible ? "95%" : "80%"} mx="auto" align="stretch" spacing={0}>
          <Box flex="1" minH="0" />
          {chatMessages.map((msg, index) => (
            <MessageComponent
              key={index}
              author={msg.author as "user" | "ai"}
              content={msg.content}
              responseMessage={msg.responseMessage}
              artifacts={msg.artifacts}
              langgraphMessageJsonList={msg.langgraph_message_json_list}
              onArtifactClick={onArtifactClick}
              onLanggraphMessageClick={onLanggraphMessageClick}
              isLoading={isLoading && index === chatMessages.length - 1 && msg.author === "ai"}
              isLastMessage={index === chatMessages.length - 1}
              lsTraceUrl={isFromEva ? msg.ls_trace_url : undefined}
              isArtifactsPanelVisible={isArtifactsPanelVisible}
              allArtifactsForLinks={allArtifacts}
            />
          ))}
          <div ref={messagesEndRef} />
        </VStack>
      </Box>
      <Box w="100%" p={4} bg="bg.surface" display="flex" justifyContent="center" mb={isArtifactsPanelVisible ? 1 : 5}>
        {renderInputArea(isArtifactsPanelVisible ? "90%" : "80%")}
      </Box>
    </VStack>
  );
};

export default Chat;